import {
	Modal,
	ModalDialog,
	ModalClose,
	Typography,
	Box,
	Button,
} from "@mui/joy";
import React from "react";

import { CodeEditor } from "../../../shared/CodeEditor";
import { LoaderWithText } from "../../../shared/Loader";
import { type TContentPart } from "../../../types/agent";

import { getCodeModalWidth } from "./codeModalSizing";

type TEditCodeModalProps = {
	open: boolean;
	onClose: () => void;
	code: TContentPart | null;
	onSave: (newCode: string) => Promise<void>;
	loading: boolean;
	error: string | null;
};

const EditCodeModal: React.FC<TEditCodeModalProps> = ({
	open,
	onClose,
	code,
	onSave,
	loading,
	error,
}) => {
	const [editedCode, setEditedCode] = React.useState("");
	const [fetchLoading, setFetchLoading] =
		React.useState(false);
	const [fetchError, setFetchError] = React.useState<
		string | null
	>(null);
	const modalWidth = React.useMemo(
		() => getCodeModalWidth(editedCode),
		[editedCode]
	);

	React.useEffect(() => {
		const fetchAndSetCode = async () => {
			if (!code) {
				setEditedCode("");
				return;
			}

			// Always try to fetch from blob_link first if available
			if (code.blob_link) {
				setFetchLoading(true);
				setFetchError(null);
				try {
					const response = await fetch(code.blob_link);
					if (!response.ok) {
						throw new Error("Failed to fetch code content");
					}
					const text = await response.text();
					let cleanCode = text;
					if (cleanCode.startsWith("```")) {
						cleanCode = cleanCode.replace(/^```\w*\n?/, "");
						cleanCode = cleanCode.replace(/\n?```$/, "");
					}
					setEditedCode(cleanCode.trim());
					setFetchLoading(false);
					return;
				} catch (err: unknown) {
					console.error(
						"Error fetching code content from blob:",
						err
					);
					setFetchLoading(false);
					// Fall through to use inline content as fallback
				}
			}

			// Fallback to inline content if blob_link not available or fetch failed
			if (code.content) {
				let cleanCode = code.content;
				if (cleanCode.startsWith("```")) {
					cleanCode = cleanCode.replace(/^```\w*\n?/, "");
					cleanCode = cleanCode.replace(/\n?```$/, "");
				}
				setEditedCode(cleanCode.trim());
			}
		};

		void fetchAndSetCode();
	}, [code]);

	const handleSave = async () => {
		await onSave(editedCode);
	};

	return (
		<Modal
			open={open}
			onClose={onClose}
		>
			<ModalDialog
				sx={{
					width: modalWidth,
					maxWidth: "calc(100vw - 48px)",
					height: "85vh",
				}}
			>
				<ModalClose />
				<Typography
					level="h4"
					sx={{ mb: 2 }}
				>
					Edit and Re-run Code
				</Typography>

				{(error ?? fetchError) && (
					<Box
						sx={{
							p: 2,
							mb: 2,
							borderRadius: 1,
							bgcolor: "#fef3f2",
							color: "#b42318",
							border: "1px solid #fecdca",
							maxHeight: "200px",
							overflowY: "auto",
						}}
					>
						{error ?? fetchError}
					</Box>
				)}

				{fetchLoading ? (
					<Box
						sx={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							minHeight: "400px",
						}}
					>
						<LoaderWithText
							withText={true}
							text="Loading code..."
							loaderWidth="30"
						/>
					</Box>
				) : (
					<div className="code-editor-wrapper">
						<CodeEditor
							value={editedCode}
							setValue={setEditedCode}
							readOnly={loading}
							showLineNumber={true}
							minHeight="400px"
							maxHeight="64vh"
							placeholder="Enter your Python code here..."
						/>
					</div>
				)}

				<Box
					sx={{
						display: "flex",
						justifyContent: "flex-end",
						gap: 1,
						mt: 2,
					}}
				>
					<Button
						onClick={onClose}
						disabled={loading || fetchLoading}
						variant="soft"
						color="neutral"
					>
						Cancel
					</Button>
					<Button
						onClick={handleSave}
						disabled={loading || fetchLoading}
					>
						{loading ? (
							<LoaderWithText
								withText={false}
								loaderWidth="16"
							/>
						) : (
							"Save & Re-run"
						)}
					</Button>
				</Box>
			</ModalDialog>
		</Modal>
	);
};

export default EditCodeModal;
